import { I18n } from 'react-redux-i18n';
import cx from 'classnames';
import debounce from 'lodash/debounce';

import ActionsButton, { TRIGGER_TYPE } from '@common/components/ActionsButton';
import { getTestAttributes } from '@common/utils/tests';
import { InfoIcon } from '@common/components/info-icon';
import { Loading } from "@common/components/loading";
import { Flex } from "@common/components/Flex";
import { IndividualGender } from "@common/dto/individual/gender";
import { NewTable, NewTableColumnPredefinedActionType, NewTableHeaderPredefinedActionType } from '@common/components/new-table';
import { getQuestionnaires, launchNewIndividualQuestionnaire } from '@common/actions/questionnaires';
import { getProfile, getUsername } from '@common/actions/profile';

import { MODULE_NAME } from '../../individuals';
import { ProfileSelector } from './components/profile-selector';
import { validateIndividualEmail, openIndividualDatasheet, moveIndividualToNewPedigree, moveIndividualToExistingPedigree, getIndividualSecurity, updateIndividualSecurity, transmitEvent } from '../../actions/individuals';
import { getModuleFolders } from '@common/actions/folders';
import { getFolderFields } from '@common/actions/formats';
 
import { IndividualRenaming } from './components/individual-renaming';
import { createBatchLetters } from '@common/actions/letters';
import { BodyNameCell } from '@common/components/body-name-cell';
import { CONTEXT_TYPE } from '../../constants/context';
import { isSmartListDateFieldType, renderSmartListDateCell } from '@common/utils/smart-list-table-cells';
import { NEW_INVITE_STEPS, NewInvite } from '@common/components/new-invite';
import { InfoMessage } from '@common/components/info-message';
import { ItemSecurityManagement } from '@common/components/item-security-management';
import { MoveToPedigree } from './components/move-to-pedigree';
import {
  generateLetterFromIndividual,
  getFamilyIdMembersCount,
  getIndividualEmail,
  openDatasheetFromIndividual,
  openPedigreeFromIndividual,
  openTestResultsFromIndividual,
  saveIndividualEmail,
  sendInvite
} from '@common/actions/individuals';
import { AdminSmartListFunctionType } from '@common/dto/admin-function/type';
import { NewQuestionnaire } from '@common/components/new-questionnaire';
import { checkPedigreeNameExists, createPedigreeAndMoveToFolder, getPedigreeFolders } from '@common/actions/pedigrees';

import './individuals-table.styles.scss';

export class IndividualsTable extends React.Component {
  profileSelectorTriggerType = {
    defaultColumns: 'defaultColumns',
    currentColumns: 'currentColumns',
  };
  profileSelectorTrigger;

  renderActionsCell = ({ row: { original } }) => {
    return (
      <ActionsButton 
        withStopPropagation
        stopPropagationOnRoot
        withStopPropagationOnBackdrop
        id={original.id} 
        data={original} 
        menuItems={this.getActions(original)}
        setCurrent={this.handleSetCurrentSelectedRow}
      />
    );
  }

  renderIndividualNameBodyCell = ({ cell }) => {
    return (
      <BodyNameCell
        item={cell.row.original}
        label={cell.row.original.name}
        onClick={openDatasheetFromIndividual} 
      />
    );
  };

  get columns() {
    const defaultWidth = 100;
    const nameCellId = 18;

    return [
      { 
        disableActions: true,
        enableResizing: false,
        enableSorting: false,
        header: 'Actions', 
        accessorKey: 'ACTIONS_COLUMNS_LOCAL',
        id: 'ACTIONS_COLUMNS_LOCAL',
        cell: this.renderActionsCell,
        minSize: 65,
        size: 65,
        className: 'table-actions-cell'
      },
      ...this.props.columns.map((x) => ({
        id: this.props.getColumnId(x),
        accessorKey: x.field.id.toString(),
        enableDragAndDrop: true,
        enableSorting: true,
        minSize: 20,
        size: x.width || defaultWidth,
        header: x.field.name,
        cell:
          x.field.id === nameCellId
            ? this.renderIndividualNameBodyCell
            : isSmartListDateFieldType(x.field.type)
              ? renderSmartListDateCell
              : undefined,
        filter: { 
          type: x.field.type,
          options: x.field.options,
          outputDateFormat: 'YYYY-MM-DD'
        }
      }))
    ];
  }

  state = {
    notificationProps: {
      opened: false,
      message: undefined,
    },
    profileSelectorOpened: false,
    fieldChooserOpened: false,
    adminSmartListFunctions: {},
    renamingIndividual: {
      opened: false,
      defaultValue: '',
      id: null,
    },
    newQuestionnaireDialogProps: {
      individualName: '',
      individualId: null,
      familyId: null,
      isSinglet: false,
      opened: false,
    },
    batchNewInviteProps: {
      opened: false,
    },
    individualSecurityProps: {
      opened: false
    },
    moveToPedigreeDialogProps: {
      opened: false,
      pedigreeExists: false
    },
    currentUserIsAdmin: false,
    hasEmail: true,
    questionnaire: null,
  };

  componentDidMount() {
    this.props.getAdminSmartListFunctions().then((adminSmartListFunctions) => {
      this.setState((x) => ({ ...x, adminSmartListFunctions }));
    });
    
    this.initUserAdminInfo();
  }

  initUserAdminInfo = async () => {
    try {
      const username = await getUsername();
      const profile = await getProfile(username);

      this.setState((x) => ({ ...x, currentUserIsAdmin: profile.admin }));
    } catch (err) {
      console.error(err);
      this.props.showErrorMessage(err?.entity || I18n.t('Failed to get profile'));
    }
  }

  handleNewQuestionnaireOpen = (id, data) => {
    this.setState((x) => ({
      ...x,
      newQuestionnaireDialogProps: {
        ...x.newQuestionnaireDialogProps,
        individualName: data.name,
        individualId: data.id,
        familyId: data.familyId,
        isSinglet: data.singlet === 'true',
        opened: true,
      },
    }));
  }

  handleNewQuestionnaireSubmit = async (questionnaire) => {
    const result = await getIndividualEmail({fhq4: questionnaire?.fhq4, individualId: this.state.newQuestionnaireDialogProps.individualId, questionnaireId: questionnaire?.questionnaireId})
    if (!result) {
      this.setState({hasEmail: false, questionnaire: questionnaire});
    }
    else {
      const redirectUrl = await launchNewIndividualQuestionnaire(
          this.state.newQuestionnaireDialogProps.individualId,
          questionnaire.questionnaireId,
          questionnaire.fhq4
      );
      window.location.href = redirectUrl;
    }
  }

  closeNewQuestionnaireDialog = () => {
    this.setState((x) => ({
      ...x,
      hasEmail: true,
      newQuestionnaireDialogProps: {
        ...x.newQuestionnaireDialogProps,
        individualName: '',
        individualId: null,
        familyId: null,
        isSinglet: false,
        opened: false,
      },
    }));
  }

  handleSetCurrentSelectedRow = (_, individual) => {
    const isMultiSelection = (this.props.selectedIndividuals?.length || 0) > 1;
    const isCurrentInSelectedAlready = this.props.selectedIndividuals?.some((x) => x[this.props.customIndividualUniqProperty] === individual[this.props.customIndividualUniqProperty]);

    if (isCurrentInSelectedAlready && isMultiSelection) {
      return;
    }

    this.props.onSelectedIndividualsChange([individual]);
  }

  handleSelectedItemsChange = (allSelectedItems) => {
    const { onSelectedIndividualsChange } = this.props;

    onSelectedIndividualsChange(allSelectedItems);
  }

  getTrProps = (state, rowInfo) => {
    const { index } = rowInfo;

    return {
      ...getTestAttributes(MODULE_NAME, 'item', 'table-row', index)
    };
  }

  getTheadThProps = (tableData, rowData, { id }) => getTestAttributes(MODULE_NAME, 'item', 'table-header-cell', id);

  getTdProps = (tableData, { index }, { id }) => getTestAttributes(MODULE_NAME, 'item', 'table-cell', `${index}-${id}`);

  handleColumnResize = (columns) => {
    return this.props.onResize(columns);
  }

  handleColumnsOrderChange = (columnsId) => {
    const columns = []
    
    columnsId.forEach((id) => {
      const column = this.props.columns.find((x) => this.props.getColumnId(x) === id);

      if (column) {
        columns.push(column);
      }
    });

    return this.props.onColumnsChange(columns);
  }

  openProfileSelector = () => {
    this.setState((x) => ({ ...x, profileSelectorOpened: true }));
  }

  closeProfileSelector = () => {
    this.setState((x) => ({ ...x, profileSelectorOpened: false }));
  }

  handleSetDefaultForSpecificUsers = () => {
    this.openProfileSelector();
    this.profileSelectorTrigger = this.profileSelectorTriggerType.defaultColumns;
  }

  handleApplyCurrentColumnsToSpecificUsers = () => {
    this.openProfileSelector();
    this.profileSelectorTrigger = this.profileSelectorTriggerType.currentColumns;
  }
  
  handleColumnAdd = ({ field, columnId }) => {
    const columnIndex = this.columns.findIndex((x) => x.id === columnId);

    const column = {
      width: 100,
      field: {
        id: field.fieldId,
        name: field.fieldName,
        context: field.fieldContext,
        type: field.fieldTypeId,
        system: field.systemField
      },
      sortOrder: 0,
      sortAsc: undefined

    };
    const columnsCopy = [...this.props.columns];
    
    columnsCopy.splice(columnIndex, 0, column);
    
    return this.props.onColumnsChange(columnsCopy);
  }

  handleColumnRemove = (columnId) => {
    const columnIndex = this.columns.findIndex((x) => x.id === columnId);
    const columnsCopy = [...this.props.columns];
    const actionsColumn = 1;

    columnsCopy.splice(columnIndex - actionsColumn, 1);

    return this.props.onColumnsChange(columnsCopy);
  }

  closeFieldChooser = async () => {
    this.setState((x) => ({ ...x, fieldChooserOpened: false }));
  }

  handleProfileSelectorSubmit = async (users) => {
    const handlerConfig = {
      [this.profileSelectorTriggerType.currentColumns]: this.props.onApplyCurrentColumnsToSpecificUsers,
      [this.profileSelectorTriggerType.defaultColumns]: this.props.onSetDefaultForSpecificUSers,
    };
    const handle = handlerConfig[this.profileSelectorTrigger];
    await handle(users);
    this.closeProfileSelector();
  }

  handleFieldChange = (field) => {

  }

  handleOpenDatasheet = (id) => {
    openDatasheetFromIndividual(id);
  }

  handleOpenPedigree = (id) => {
    openPedigreeFromIndividual(id);
  }

  handleOpenTestResults = (id) => {
    openTestResultsFromIndividual(id);
  }

  handleRenameIndividualClick = (id, individual) => {
    this.setState((x) => ({
      ...x,
      renamingIndividual: {
        defaultValue: individual.name,
        opened: true,
        id: individual.id,
      },
    }));
  }

  closeRenamingIndividual = () => {
    this.setState((x) => ({
      ...x,
      renamingIndividual: {
        defaultValue: '',
        opened: false,
        id: null,
      },
    }));
  }

  handleRenameIndividual = async (name) => {
    await this.props.onRenameIndividual(this.state.renamingIndividual.id, name);
    this.closeRenamingIndividual();
    this.props.showMessage({
      header: I18n.t('Information Message'),
      content: <><InfoIcon />  {I18n.t('Individual successfully renamed')}</>,
    });
  }

  handleGenerateLetter = (id, individual) => {
    generateLetterFromIndividual(individual.id);
  }

  handleNewInviteOpen = (individualId) => {
    this.props.onNewInviteOpen(individualId);
  }

  handleQuestionnaireOpen = (id, data) => {
    this.props.onQuestionnaireOpen({
      id: data.id,
      name: data.name,
    });
  }

  getMovePedigreeOptions() {
    return [
      { label: I18n.t("Existing Pedigree"), onClick: this.handleMoveToExistingPedigreeClick },
      { label: I18n.t("New Pedigree"), onClick: this.handleMoveToNewPedigreeClick },
    ];
  }

  handleDeleteIndividualClick = (id, data) => {
    this.props.showMessage({
      header: I18n.t('Confirmation Message'),
      confirmText: I18n.t('Yes'),
      cancelText: I18n.t('No'),
      show: true,
      content: <InfoMessage>{I18n.t('Are you sure you wish to delete the selected Individual?')}</InfoMessage>,
      onCustomOk: async () => {
        try {
          this.props.closeMessage();
          await this.props.onIndividualDelete(data.familyId);
        } catch (err) {
          console.error(err);
          this.props.showErrorMessage(err?.entity || I18n.t('Failed to delete individual'));
        }
      }
    });
  }

  handleGenderChange = async (individualId, gender, label) => {
    try {
      await this.props.onIndividualGenderChange(individualId, gender);
      this.props.showInfoMessage(`${I18n.t('Individual sex successfully changed to')} ${label}`);
    } catch (err) {
      console.error(err);
      this.props.showErrorMessage(err?.entity || I18n.t('Failed to change sex'));
    }
  }

  getSexOptions = (individualId, gender) => {
    const allGenders = [IndividualGender.female, IndividualGender.male, IndividualGender.unknown];
    const labels = {
      [IndividualGender.female]: 'Female',
      [IndividualGender.male]: 'Male',
      [IndividualGender.unknown]: 'Unknown'
    };

    const options = allGenders.filter((x) => x.toString() !== gender.toString()).map((gender) => ({
      label: I18n.t(labels[gender]),
      onClick: () => this.handleGenderChange(individualId, gender, I18n.t(labels[gender])),
    }));

    return options;
  }

  openIndividualSecurity = (id, data) => {
    this.setState((x) => ({
      ...x,
      individualSecurityProps: {
        opened: true,
        familyId: data.familyId,
      },
    }));
  }

  closeIndividualSecurity = () => {
    this.setState((x) => ({
      ...x,
      individualSecurityProps: {
        opened: false,
      },
    }));
  }

  toggleMoveToPedigreeDialog = ({ opened, pedigreeExists = false }) => {
    this.setState((x) => ({
      ...x,
      moveToPedigreeDialogProps: {
        ...x.moveToPedigreeDialogProps,
        opened,
        pedigreeExists
      },
    }));
  }

  closeMoveToPedigreeDialog = () => {
    this.toggleMoveToPedigreeDialog({ opened: false });
  }

  handleSubmitMoveToPedigree = () => {
    this.closeMoveToPedigreeDialog();
    this.props.showInfoMessage(I18n.t("Individuals moved successfully"));
  }

  handleMoveToExistingPedigreeClick = () => {
    this.toggleMoveToPedigreeDialog({ opened: true, pedigreeExists: true });
  }

  handleMoveToNewPedigreeClick = () => {
    this.toggleMoveToPedigreeDialog({ opened: true });
  }

  getTransmitEvents = () => {
    if (window.m_init_details?.length) {
      let events = window.m_init_details[0].TRANSMIT_CUSTOM_INTERGRATION_EVENTS;

      if (events === 'null' || !events) {
        return '';
      }

      return events;
    }
  }

  transmitEvent = async (eventCode, individualId) => {
    try {
      const res = await transmitEvent({ eventCode, individualId, token: this.props.loginToken });

      if (res.code === 200) {
        this.setState((x) => ({
          ...x,
          notificationProps: {
            opened: true,
            message: I18n.t('Transmit Events saved successfully')
          }
        }));

        setTimeout(() => {
          this.setState((x) => ({
            ...x,
            notificationProps: {
              opened: false,
              message: undefined
            }
          }));
        }, 2000);
      } else {
        this.props.showErrorMessage(I18n.t('Failed to transmit event'));
      }
    } catch (err) {
      console.error('Failed to transmit event', err);
      this.props.showErrorMessage(err?.entity || I18n.t('Failed to transmit event'));
    }
  }

  getTransmitEventsOptions = ({ id }) => {
    try {
      const description = this.getTransmitEvents();

      if (!description) {
        return [];
      }
  
      const transmitEvents = description.split(';').reduce((acc, transmitEvent) => {
        let values = transmitEvent.split('|');
    
        if (values?.length === 2) {
          acc.push({ 
            label: values[0], 
            onClick: () => this.transmitEvent(values[1], id)
          });
        }

        return acc;
      }, []);
  
      return transmitEvents;
    } catch (err) {
      console.error('failed to parse transmit events', err);
      return [];
    }
  }

  getActions({ invitesCount, id, singlet, gender, writeAllowed, proband }) {
    if ((this.props.selectedIndividuals?.length || 0) > 1) {
      return this.rowContextMenuActions;
    }

    invitesCount = invitesCount ? Number(invitesCount) : 0;

    const isSinglet = singlet === "true";
    const canWrite = writeAllowed === "true";
    const transmitEventsOptions = this.getTransmitEventsOptions({ id });

    return [
      { label: "Open Datasheet", onClick: this.handleOpenDatasheet },
      { label: 'Open Pedigree', onClick: this.handleOpenPedigree, condition: !isSinglet },
      { label: "Open Test Results", onClick: this.handleOpenTestResults },
      { 
        label: (
          <ActionsButton
              withBackdrop={false}
              className="PedigreesIndividualsTable__update-status__btn"
              classes={{
                menuContainer: "PedigreesIndividualsTable__update-status__menu",
              }}
              id="ind-home-tab-list-act-menubar-item3"
              triggerType={TRIGGER_TYPE.hover}
              trigger={<><span className="IndividualsTable__nested-menu-btn-label">{I18n.t("Move to pedigree")}</span> <span className="fa fa-angle-right" /></>}
            >
              {this.getMovePedigreeOptions().map((option, index) => (
                <div 
                  key={index}
                  className="PedigreesIndividualsTable__update-status__menu__item"
                  onClick={option.onClick}
                >
                  {option.label}
                </div>
              ))}
          </ActionsButton>
        ), 
        condition: canWrite && isSinglet
      },
      { label: "Rename Individual", onClick: this.handleRenameIndividualClick },
      { 
        label: (
          <ActionsButton
            withBackdrop={false}
            className="PedigreesIndividualsTable__update-status__btn"
            classes={{
              menuContainer: "PedigreesIndividualsTable__update-status__menu",
            }}
            id={`change-sex-actions-${id}`}
            triggerType={TRIGGER_TYPE.hover}
            trigger={<><span className="IndividualsTable__nested-menu-btn-label">{I18n.t('Change Sex')}</span> <span className="fa fa-angle-right" /></>}
          >
            {this.getSexOptions(id, gender).map((option, index) => (
              <div 
                key={index}
               className="PedigreesIndividualsTable__update-status__menu__item"
                onClick={option.onClick}
              >
                {option.label}
              </div>
            ))}
        </ActionsButton>  
        ), 
        condition: isSinglet
      },
      { label: 'Delete Individual', onClick: this.handleDeleteIndividualClick, condition: isSinglet },
      { label: 'New Invite', onClick: this.handleNewInviteOpen, },
      { label: 'Launch New Questionnaire', onClick: this.handleNewQuestionnaireOpen, },
      { label: 'Edit Individual Security', onClick: this.openIndividualSecurity, condition: isSinglet && this.state.currentUserIsAdmin },
      { label: 'Open Questionnaire', onClick: this.handleQuestionnaireOpen, condition: invitesCount > 0 },
      { label: 'Generate Letter', onClick: this.handleGenerateLetter },
      {
        label: (
          <ActionsButton
            withBackdrop={false}
            className="PedigreesIndividualsTable__update-status__btn"
            classes={{
              menuContainer: "PedigreesIndividualsTable__update-status__menu",
            }}
            id={`transmit-events-actions-${id}`}
            triggerType={TRIGGER_TYPE.hover}
            trigger={<><span className="IndividualsTable__nested-menu-btn-label">{I18n.t('Transmit Events')}</span> <span className="fa fa-angle-right" /></>}

          >
            {transmitEventsOptions.map((option, index) => (
              <div 
                key={index}
                className="PedigreesIndividualsTable__update-status__menu__item"
                onClick={option.onClick}
              >
                {option.label}
              </div>
            ))}
          </ActionsButton>
        ),
        condition: proband === 'true' && transmitEventsOptions.length > 0
      },
    ].filter(({ condition = true }) => condition).map((x) => ({ ...x, label: typeof x.label === 'string' ? I18n.t(x.label) : x.label }));
  }

  get columnActions() {
    const { onSetDefaultForAllUSers, onApplyCurrentColumnsToAllUsers, onRestoreColumnToDefault } = this.props;

    return [
      { type: NewTableColumnPredefinedActionType.filters },
      { feature: AdminSmartListFunctionType.setDefaultForAllUsers, label: I18n.t('Set as Default for All Users'), className: 'IndividualsTable__column-action-top-divider', onClick: onSetDefaultForAllUSers },
      { feature: AdminSmartListFunctionType.setDefaultForSpecificUsers, label: I18n.t('Set as Default for Specific Profiles'), onClick: this.handleSetDefaultForSpecificUsers },
      { feature: AdminSmartListFunctionType.applyCurrentColumnsToAllUsers, label: I18n.t('Apply Current Columns Now to All Users'), onClick: onApplyCurrentColumnsToAllUsers },
      { feature: AdminSmartListFunctionType.applyCurrentColumnsToSpecificProfiles, label: I18n.t('Apply Current Columns Now to Specific Profiles'), onClick: this.handleApplyCurrentColumnsToSpecificUsers },
      { feature: AdminSmartListFunctionType.restoreColumnsToDefault, label: I18n.t('Restore Columns to Default'), onClick: onRestoreColumnToDefault },
      { feature: AdminSmartListFunctionType.addColumns, type: NewTableColumnPredefinedActionType.addColumn, className: 'IndividualsTable__column-action-top-divider' },
      { feature: AdminSmartListFunctionType.deleteColumn, type: NewTableColumnPredefinedActionType.deleteColumn },
      { type: NewTableColumnPredefinedActionType.clearSorting }
    ].filter(({ feature }) => feature ? this.state.adminSmartListFunctions[feature] : true).map(({ feature, ...otherProps }) => otherProps);
  }

  get headerActions() {
    const { onSetDefaultForAllUSers, onApplyCurrentColumnsToAllUsers, onRestoreColumnToDefault } = this.props;

    return [
      { feature: AdminSmartListFunctionType.setDefaultForAllUsers, label: I18n.t('Set as Default for All Users'), onClick: onSetDefaultForAllUSers },
      { feature: AdminSmartListFunctionType.setDefaultForSpecificUsers, label: I18n.t('Set as Default for Specific Profiles'), onClick: this.handleSetDefaultForSpecificUsers },
      { feature: AdminSmartListFunctionType.applyCurrentColumnsToAllUsers, label: I18n.t('Apply Current Columns Now to All Users'), onClick: onApplyCurrentColumnsToAllUsers },
      { feature: AdminSmartListFunctionType.applyCurrentColumnsToSpecificProfiles, label: I18n.t('Apply Current Columns Now to Specific Profiles'), onClick: this.handleApplyCurrentColumnsToSpecificUsers },
      { feature: AdminSmartListFunctionType.restoreColumnsToDefault, label: I18n.t('Restore Columns to Default'), onClick: onRestoreColumnToDefault },
      { feature: AdminSmartListFunctionType.addColumns, type: NewTableHeaderPredefinedActionType.addColumn, className: 'IndividualsTable__column-action-top-divider' },
    ].filter(({ feature }) => feature ? this.state.adminSmartListFunctions[feature] : true).map(({ feature, ...otherProps }) => otherProps);
  }

  handleGetFields = ({ searchKeyword, folderId, }) => {
    const noFolderSelected = 0;

    return getFolderFields({ 
      searchQuery: searchKeyword, 
      folderId: folderId || noFolderSelected, 
      contextId: CONTEXT_TYPE.individual,
      includeTables: false,
    }).then((fields) => fields.map((field) => ({
      fieldId: field.id,
      fieldName: field.name,
      fieldContext: field.context,
      fieldTypeId: field.type,
      systemField: field.system,
      imageURL: field.imageURL,
      fullPath: field.fullPath,
      options: field.options,
    })));
  }

  handleBatchLetters = () => {
    if (this.props.selectedIndividuals?.length) {
      createBatchLetters(this.props.selectedIndividuals.map((x) => x.id));
    }
  }

  get rowContextMenuActions() {
    return [
      {
        label: I18n.t('Batch Letters'),
        onClick: this.handleBatchLetters
      },
      {
        label: I18n.t('Batch Invites'),
        onClick: this.openBatchNewInvites,
      }
    ];
  }

  sendBatchNewInvites = async (invite) => {
    const individualsEmailsValidationResponses = await Promise.all((this.props.selectedIndividuals || []).map(async ({ id }) => {
      const response = await validateIndividualEmail({
        individualId: id,
        fhq4: invite.fhq4,
        questionnaireId: invite.questionnaireId,
      });

      return {
        id,
        response
      };
    }));
    const individualsWithError = individualsEmailsValidationResponses
      .filter((x) => x.response.code !== 200)
      .reduce((acc, x) => {
        const individual = this.props.data.find(({ id }) => id === x.id);

        if (individual) {
          acc.push(individual);
        }

        return acc;
      }, []);

    if (individualsWithError.length) {
      throw {
        entity: (
          <div>
            <p>
              {I18n.t('There is a problem with the email addresses for the selected individuals')}. {I18n.t('Invitations were not sent to the following individuals:')}
            </p>
            <p>
              {individualsWithError.map((x) => <p key={x.id}>{x.name}</p>)}
            </p>
            <p>
            {I18n.t('Please enter valid email addresses and resend the invitations')}
            </p>
          </div>
        )
      };
    }

    await Promise.all(this.props.selectedIndividuals.map(({ id }) => {
      const copy = { ...invite };
      copy.globalId = id;

      return sendInvite(copy);
    }));
    
    this.props.showInfoMessage(I18n.t('The Questionnaire invitation emails were successfully submitted'));
    this.closeBatchNewInvites();
  }

  closeBatchNewInvites = () => {
    this.setState((x) => ({
      ...x,
      batchNewInviteProps: {
        opened: false,
      },
    }));
  }

  openBatchNewInvites = () => {
    if (this.props.selectedIndividuals?.length > 1) {
      this.setState((x) => ({
        ...x,
        batchNewInviteProps: {
          opened: true,
        },
      }));
    }
  }

  getBatchNewInvitesSteps = () => {
    return [
      {
        condition: true,
        step: NEW_INVITE_STEPS.questionnaires,
      },
      {
        condition: true,
        step: NEW_INVITE_STEPS.emailTemplate,
      }
    ];
  }

  getData = (params) => {
    const { filtering, sorting, pagination } = params;

    const processedSorting = sorting
      ? {
        ...sorting,
        index: this.columns.findIndex((x) => x.id === sorting.id),
      }
      : undefined

    return this.props.getData({
      filtering: filtering,
      pagination,
      sorting: processedSorting,
    });
  }

  getQuestionnairesForBatchInvites = () => {
    return getQuestionnaires({
      includeFamilyQuestionnaires: false,
      includeFhq4Questionnaires: true,
      includeHiddenQuestionnaires: false
    });
  }

  getHeaderElementId = () => {
    return 'ind-home-tab-list-grid-thead-row0';
  }

  getHeaderCellElementId = (column, columnIndex) => {
    return `ind-home-tab-list-grid-thead-row0-cell${columnIndex}`;
  }

  getBodyRowElementId = (dataItem, rowIndex) => {
    return `ind-home-tab-list-grid-tbody-row${rowIndex}`;
  }

  getBodyCellElementId = (column, columnIndex, dataItem, rowIndex) => {
    return `ind-home-tab-list-grid-tbody-row${rowIndex}-cell${columnIndex}`;
  }

  getIndividualSecurity = () => {
    return getIndividualSecurity(this.state.individualSecurityProps.familyId);
  }

  updateIndividualSecurity = async (options) => {
    await updateIndividualSecurity(this.state.individualSecurityProps.familyId, options);
    this.closeIndividualSecurity();
    this.props.showInfoMessage(I18n.t('Security settings were successfully changed'));
  }

  getModuleFoldersForColumns = () => {
    const individualsId = 1;

    return getModuleFolders({ folderTypeId: individualsId, setUp: { rootFolderImgUrl: 'gif/df_individual.gif', rootFolderName: I18n.t('Individual Data Folders') } });
  }

  handleGetQuestionnaires = async () => {
    const { familyId, isSinglet } = this.state.newQuestionnaireDialogProps;
    const membersCount = await getFamilyIdMembersCount(familyId);
    const questionnaires = await getQuestionnaires({
      includeFamilyQuestionnaires: !isSinglet && membersCount <= 1,
      includeFhq4Questionnaires: true,
      includeHiddenQuestionnaires: false
    });

    return questionnaires;
  }

  render() {
    const {
      className, 
      data,
      totalCount,
      getData, 
      loadMetadata, 
      filters, 
      getProfiles, 
      showErrorMessage, 
      showInfoMessage,
      selectedIndividuals,
      loading,
      reference,
    } = this.props;

    return (
      <div className={cx('IndividualsTable', className)}>
        {/* TODO */}
        {/* {this.state.notificationProps.opened && (
          <Alert bsStyle="success" bsSize="medium" className="IndividualsTable__success-message" children={this.state.notificationProps.message} />
        )} */}
        {this.state.individualSecurityProps.opened && (
          <ItemSecurityManagement
            getId={{
              title:'lbl-ped-sec-close',
              cancelBtn: 'btn-ped-sec-cancel',
              submitBtn: 'btn-ped-sec-save',
            }}
            title={I18n.t("Edit Pedigree Security")}
            overriddenSubtitle={I18n.t('Security for this pedigree is overridden at the folder level')}
            getSecurity={this.getIndividualSecurity}
            onSubmit={this.updateIndividualSecurity}
            onClose={this.closeIndividualSecurity}
          />
        )}
        {
          loading && (
            <Flex width="100%" justify="center" alignItems="center" className="IndividualsTable__loading-content">
              <Loading size={25}/>
            </Flex>
          )
        }
        {this.state.moveToPedigreeDialogProps.opened && (
          <MoveToPedigree
            folders={this.props.folders}
            pedigreeExists={this.state.moveToPedigreeDialogProps.pedigreeExists}
            onClose={this.closeMoveToPedigreeDialog}
            onSubmit={this.handleSubmitMoveToPedigree}
            individual={this.props.selectedIndividual}
            showErrorMessage={showErrorMessage}
            moveIndividualToNewPedigree={moveIndividualToNewPedigree}
            moveIndividualToExistingPedigree={moveIndividualToExistingPedigree}
          />
        )}
        {this.state.newQuestionnaireDialogProps.opened && (
          <NewQuestionnaire
            key={this.state.hasEmail}
            moduleName={MODULE_NAME}
            title={`${I18n.t("New Questionnaire for")} ${this.state.newQuestionnaireDialogProps.individualName}`}
            showErrorMessage={showErrorMessage}
            onClose={this.closeNewQuestionnaireDialog}
            getQuestionnaires={this.handleGetQuestionnaires}
            onSubmit={this.handleNewQuestionnaireSubmit}
            hasEmail={this.state.hasEmail}
            newQuestionnaire={this.state.questionnaire}
            individualId={this.state.newQuestionnaireDialogProps.individualId}
          />
        )}
        {this.state.profileSelectorOpened && (
          <ProfileSelector 
            getData={getProfiles}
            onClose={this.closeProfileSelector}
            onSubmit={this.handleProfileSelectorSubmit}
            showErrorMessage={showErrorMessage}
          />
        )}
        {this.state.renamingIndividual.opened && (
          <IndividualRenaming 
            defaultValue={this.state.renamingIndividual.defaultValue}
            onSubmit={this.handleRenameIndividual}
            onClose={this.closeRenamingIndividual}
            showErrorMessage={showErrorMessage}
          />
        )}
        {this.state.batchNewInviteProps.opened && (
          <NewInvite
            getQuestionnaires={this.getQuestionnairesForBatchInvites}
            showErrorMessage={showErrorMessage}
            getSteps={this.getBatchNewInvitesSteps}
            onSubmit={this.sendBatchNewInvites}
            onClose={this.closeBatchNewInvites}
            saveIndividualEmail={saveIndividualEmail}
          />
        )}
        <NewTable 
          infiniteScroll
          disableAutoInit
          tableRef={reference}
          classes={{
            root: 'IndividualsTable__component'
          }}
          loading={{ list: loading }}
          columnActions={this.columnActions}
          headerActions={this.headerActions}
          columnsResizing={{
            onChange: this.handleColumnResize,
          }}
          columnsOrder={{
            onChange: this.handleColumnsOrderChange
          }}
          getId={{
            header: this.getHeaderElementId,
            headerCell: this.getHeaderCellElementId,
            bodyRow: this.getBodyRowElementId,
            bodyCell: this.getBodyCellElementId,
          }}
          rowActions={selectedIndividuals?.length > 1 ? this.rowContextMenuActions : undefined}
          selection={{
            type: 'multiple',
            items: selectedIndividuals,
            onChange: this.handleSelectedItemsChange
          }}
          moduleName={`${MODULE_NAME}_individuals_table`}
          filters={{
            operators: filters,
          }}
          noDataMessage={I18n.t('There no individuals to display')}
          errorMessage={{ list: I18n.t('Failed to load individuals') }}
          itemUniqProperty={this.props.customIndividualUniqProperty}
          data={data}
          columns={this.columns}
          totalItemsCount={totalCount}
          loadMetadata={loadMetadata}
          columnsManagement={{
            onAdd: this.handleColumnAdd,
            onRemove: this.handleColumnRemove,
            getFolders: this.getModuleFoldersForColumns,
            getFields: this.handleGetFields,
          }}
          onLoadData={this.getData}
        />  
      </div>
    );
  }
}